import './PushSidePanel.scss';

import React, { useCallback, useMemo, useState } from 'react';
import { useAppSelector as useSelector, useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';
import LargeModal from 'components/Shared/LargeModal/LargeModal';
import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import IconPush from 'pages/FlowBuilder/assets/icons/IconPush';
import useJourneyStatus from 'hooks/useJourneyStatus';
import { useFeatureFlag } from 'feature-flags';
import PushForm from './PushForms/PushForm/PushForm';
import ContentForm from '../ContentSidePanel/ContentForm/ContentForm';
import ContentFormWithPreview from '../ContentSidePanel/ContentForm/ContentFormWithPreview';
import { LocalizedPushField, PushDraft, PushSidePanelDraft, RuleDraft } from './PushForms/pushDraft.types';
import { ActionTypes } from 'store/actionTypes';
import {
  buildUpdatePushFields,
  createEmptyDraft,
  getExistingContentRefIdForContentId,
  getLinkedContentRefIdForPush,
  seedDraftFromState,
} from './PushForms/pushDraft.helper';
import { insertNewPush, updatePushSimple } from 'store/push/push.thunk';
import { insertContents } from 'store/content/content.thunk';
import { Content } from 'interface/content/content.interface';
import { genUUID } from 'helpers/common.helper';
import { selectStepRule } from 'store/rule/rule.selector';
import TouchpointEditor from 'pages/ExperienceCanvas/TouchpointEditor/TouchpointEditor';

const MAIN_CLASS = 'push-side-panel';

function PushSidePanelLegacy() {
  const { isJourneyLocked } = useJourneyStatus();
  const flowBuilderContext = useFlowBuilderContext();
  const flowPushContext = flowBuilderContext.sidePanels.push;
  const flowContentContext = flowBuilderContext.sidePanels.content;
  const isModal = flowBuilderContext.shellVariant === 'modal';
  const closeSidePanel = useCallback(() => {
    flowPushContext.setPushContext({ isOpen: false });
    flowContentContext.setContentContext((prev) => ({ ...prev, contentManagerSingleSelectOnly: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowPushContext.setPushContext, flowContentContext.setContentContext]);

  const defaultIcon = useMemo(() => <IconPush />, []);
  const headerIcon = flowPushContext.iconOverride ?? defaultIcon;
  const headerTitle = flowPushContext.titleOverride ?? 'Push Notification';
  const headerDescription = 'Currently editing from Flow Builder';

  const touchpointEditor = (
    <TouchpointEditor
      isFromFlow
      stepIdx={flowPushContext.stepIndex}
      activeTabIdx={1}
      overrideExitPrompt
      closeFlowSidePanel={closeSidePanel}
      autoScrollToContent={flowPushContext.titleOverride === 'Content'}
      contentManagerSingleSelectOnly={!isJourneyLocked}
    />
  );

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: flowPushContext.isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: headerIcon,
        goBackTitle: headerTitle,
        goBackSubTitle: headerDescription,
        goBackActionHandler: closeSidePanel,
      },
      showFooter: false,
      size: 'large',
    }),
    [flowPushContext.isOpen, headerIcon, headerTitle, headerDescription, closeSidePanel],
  );

  return (
    <div className={MAIN_CLASS}>
      {isModal ? (
        <LargeModal
          isOpen={flowPushContext.isOpen}
          closeModal={closeSidePanel}
          headerIcon={headerIcon}
          title={headerTitle}
          description={headerDescription}
          showFooter={false}
        >
          {touchpointEditor}
        </LargeModal>
      ) : (
        <SlidingSidePanel {...slidingSidePanelProps}>{touchpointEditor}</SlidingSidePanel>
      )}
    </div>
  );
}

function PushSidePanelStandalone() {
  const dispatch = useDispatch();
  const { isJourneyLocked } = useJourneyStatus();
  const flowBuilderContext = useFlowBuilderContext();
  const flowPushContext = flowBuilderContext.sidePanels.push;
  const flowContentContext = flowBuilderContext.sidePanels.content;
  const stepIdx = flowPushContext.stepIndex;

  const rootState = useSelector((state) => state);

  const stepPushRefId = rootState.te.journey.steps[stepIdx]?.push;
  const existingPush = stepPushRefId ? rootState.te.push.byRefId[stepPushRefId] : undefined;
  const isContentSidePanel = flowPushContext.titleOverride === 'Content';
  const isReadonly = !!flowPushContext.isReadonly;

  // Modal Props
  const isModal = flowBuilderContext.shellVariant === 'modal';
  const defaultIcon = useMemo(() => <IconPush />, []);
  const headerIcon = flowPushContext.iconOverride ?? defaultIcon;
  const headerTitle = flowPushContext.titleOverride ?? 'Push Notification';
  const headerDescription = 'Currently editing from Flow Builder';

  const [draft, setDraft] = useState<PushSidePanelDraft>(createEmptyDraft);
  const [seededDraft, setSeededDraft] = useState<PushSidePanelDraft>(createEmptyDraft);
  const [prevOpen, setPrevOpen] = useState(false);

  if (flowPushContext.isOpen !== prevOpen) {
    setPrevOpen(flowPushContext.isOpen);
    if (flowPushContext.isOpen) {
      const seeded = seedDraftFromState(rootState, stepIdx);
      setDraft(seeded);
      setSeededDraft(seeded);
    }
  }

  const closeSidePanel = useCallback(() => {
    flowPushContext.setPushContext({ isOpen: false });
    flowContentContext.setContentContext((prev) => ({ ...prev, contentManagerSingleSelectOnly: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowPushContext.setPushContext, flowContentContext.setContentContext]);

  const onChangePushField = useCallback(
    (
      field: keyof PushDraft | LocalizedPushField,
      langOrValue: string | number | boolean | PushDraft[keyof PushDraft],
      value?: string,
    ) => {
      setDraft((prev) => {
        const localizedFields: LocalizedPushField[] = ['title', 'alert', 'url', 'actionLinkScheme'];
        if (
          localizedFields.includes(field as LocalizedPushField) &&
          typeof langOrValue === 'string' &&
          value !== undefined
        ) {
          const localizedField = field as LocalizedPushField;
          const currentLocalized = (prev.push[localizedField] as Record<string, string>) ?? {};
          return {
            ...prev,
            push: {
              ...prev.push,
              [localizedField]: {
                ...currentLocalized,
                [langOrValue]: value,
              },
            },
          };
        }

        return {
          ...prev,
          push: {
            ...prev.push,
            [field]: langOrValue,
          },
        };
      });
    },
    [],
  );

  const onSelectPayloadType = useCallback((key: PushDraft['pushPayloadType'], id: string) => {
    setDraft((prev) => ({
      ...prev,
      push: {
        ...prev.push,
        pushPayloadType: key,
        pushPayloadTypeId: id,
      },
    }));
  }, []);

  const onChangeRuleField = useCallback((field: keyof RuleDraft, value: boolean) => {
    setDraft((prev) => ({
      ...prev,
      rule: {
        ...prev.rule,
        [field]: value,
      },
    }));
  }, []);

  const onSelectContent = useCallback((content: Content, refId?: string) => {
    setDraft((prev) => ({
      ...prev,
      content: {
        selectedContent: content,
        selectedContentRefId: refId ?? prev.content.selectedContentRefId,
        cleared: false,
      },
    }));
  }, []);

  const onRemoveContent = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        selectedContent: undefined,
        cleared: true,
      },
    }));
  }, []);

  const handleSecondaryAction = useCallback(() => {
    setDraft(seededDraft);
    closeSidePanel();
  }, [closeSidePanel, seededDraft]);

  const handlePrimaryAction = useCallback(() => {
    dispatch((thunkDispatch, getState) => {
      let pushRefId = getState().te.journey.steps[stepIdx]?.push;

      if (!pushRefId) {
        thunkDispatch(insertNewPush(stepIdx));
        pushRefId = getState().te.journey.steps[stepIdx]?.push;
      }

      if (!pushRefId) return;

      if (draft.push.pushPayloadType === 'content') {
        const originalRefId = draft.content.selectedContentRefId ?? getLinkedContentRefIdForPush(getState(), pushRefId);

        if (draft.content.cleared) {
          if (originalRefId) {
            thunkDispatch({
              type: 'REMOVE_CONTENT',
              payload: {
                refIds: [originalRefId],
                stepIdx,
                required: [false],
              },
            });
          }
        } else if (draft.content.selectedContent?.id) {
          const contentId = draft.content.selectedContent.id;
          const existingRefId = getExistingContentRefIdForContentId(getState(), stepIdx, contentId);

          if (existingRefId) {
            if (existingRefId !== originalRefId) {
              thunkDispatch({
                type: 'CHANGE_PRIMARY_CONTENT',
                payload: {
                  oldPrimaryRefId: originalRefId ?? '',
                  newPrimaryRefId: existingRefId,
                  affectedEntities: [{ refId: pushRefId, var: '{{dep.content-id.0}}' }],
                  stepIdx,
                },
              });
            } else {
              thunkDispatch({
                type: 'UPDATE_CONTENT',
                payload: { [existingRefId]: draft.content.selectedContent },
              });
              thunkDispatch({
                type: ActionTypes.SET_UI_NODE_ATTR,
                payload: {
                  refId: existingRefId,
                  fields: { enableOptionalNode: true },
                },
              });
            }
          } else if (originalRefId) {
            thunkDispatch({
              type: 'UPDATE_CONTENT',
              payload: { [originalRefId]: draft.content.selectedContent },
            });
            thunkDispatch({
              type: ActionTypes.SET_UI_NODE_ATTR,
              payload: {
                refId: originalRefId,
                fields: { enableOptionalNode: true },
              },
            });
          } else {
            thunkDispatch(
              insertContents({
                refIds: [genUUID()],
                stepIdx,
                contents: [draft.content.selectedContent],
                dependantPushRefId: pushRefId,
              }),
            );
          }
        }
      }

      const fields = buildUpdatePushFields({ ...draft.push, refId: pushRefId });

      thunkDispatch(
        updatePushSimple(
          {
            refId: pushRefId,
            fields,
            preventAutoLinkingWithContent: !isJourneyLocked,
          },
          stepIdx,
          draft.push.pushPayloadType ?? 'basic',
        ),
      );

      const rule = selectStepRule(getState().te, stepIdx);
      const ruleRefId = draft.rule.refId ?? rule?.refId;
      if (ruleRefId && draft.rule.emitEveryEvaluationResult !== undefined) {
        thunkDispatch({
          type: 'UPDATE_RULE_SIMPLE',
          payload: {
            refId: ruleRefId,
            fields: { emitEveryEvaluationResult: draft.rule.emitEveryEvaluationResult },
          },
        });
      }

      thunkDispatch({
        type: 'UPDATE_STEP',
        payload: {
          stepIdx,
          fields: {
            isDirty: true,
          },
        },
      });
    });

    closeSidePanel();
  }, [closeSidePanel, dispatch, draft, isJourneyLocked, stepIdx]);

  const pushPayloadType = draft.push.pushPayloadType ?? 'basic';
  const showContentForm = pushPayloadType === 'content';

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: flowPushContext.isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: flowPushContext.iconOverride ?? <IconPush />,
        goBackTitle: flowPushContext.titleOverride ?? 'Push Notification',
        goBackSubTitle: isReadonly ? 'Currently viewing from Flow Builder' : 'Currently editing from Flow Builder',
        goBackActionHandler: handleSecondaryAction,
      },
      footerInfo: {
        ...(isReadonly ? {} : { primaryActionText: 'Continue', primaryActionHandler: handlePrimaryAction }),
        secondaryActionText: isReadonly ? 'Close' : 'Cancel',
        secondaryActionHandler: handleSecondaryAction,
      },
      showFooter: true,
      size: 'large',
      isViewOnly: isReadonly,
      viewOnlyHeader: {
        text: 'This is an active experience',
        subText: 'To update this push notification, please deactivate the experience first.',
      },
    }),
    [
      flowPushContext.isOpen,
      flowPushContext.iconOverride,
      flowPushContext.titleOverride,
      isReadonly,
      handlePrimaryAction,
      handleSecondaryAction,
    ],
  );

  const contentViewOnlySlidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: flowPushContext.isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: flowPushContext.iconOverride ?? <IconPush />,
        goBackTitle: flowPushContext.titleOverride ?? 'Push Notification',
        goBackSubTitle: 'Currently viewing from Flow Builder',
        goBackActionHandler: closeSidePanel,
      },
      showFooter: false,
      size: 'large',
      isViewOnly: true,
      viewOnlyHeader: {
        text: 'This is an active experience',
        subText: 'To update this content, please deactivate the experience first.',
      },
    }),
    [flowPushContext.isOpen, flowPushContext.iconOverride, flowPushContext.titleOverride, closeSidePanel],
  );

  if (isContentSidePanel)
    return (
      <div className={MAIN_CLASS}>
        <SlidingSidePanel {...contentViewOnlySlidingSidePanelProps}>
          <ContentFormWithPreview stepIdx={flowPushContext.stepIndex} />
        </SlidingSidePanel>
      </div>
    );

  return isModal ? (
    <div className={MAIN_CLASS}>
      <LargeModal
        isOpen={flowPushContext.isOpen}
        closeModal={closeSidePanel}
        headerIcon={headerIcon}
        title={headerTitle}
        description={headerDescription}
        showFooter={true}
        primaryAction={
          isReadonly
            ? undefined
            : {
                label: 'Apply',
                onClick: handlePrimaryAction,
              }
        }
        secondaryAction={{
          label: isReadonly ? 'Close' : 'Cancel',
          onClick: handleSecondaryAction,
        }}
      >
        <PushForm
          pushDraft={draft.push}
          ruleDraft={draft.rule}
          stepIdx={stepIdx}
          isJourneyLocked={isJourneyLocked}
          pushPayloadTypeConstrained={existingPush?.constraints?.pushPayloadTypeId?.isReadOnly}
          isReadonly={isReadonly}
          onChangePushField={onChangePushField}
          onSelectPayloadType={onSelectPayloadType}
          onChangeRuleField={onChangeRuleField}
        />
        {showContentForm && (
          <ContentForm
            contentDraft={draft.content}
            stepIdx={stepIdx}
            isJourneyLocked={isJourneyLocked}
            contentManagerSingleSelectOnly={!isJourneyLocked}
            stepPushRefId={stepPushRefId}
            readOnly={isReadonly}
            onSelectContent={onSelectContent}
            onRemoveContent={onRemoveContent}
          />
        )}
      </LargeModal>
    </div>
  ) : (
    <div className={MAIN_CLASS}>
      <SlidingSidePanel {...slidingSidePanelProps}>
        <PushForm
          pushDraft={draft.push}
          ruleDraft={draft.rule}
          stepIdx={stepIdx}
          isJourneyLocked={isJourneyLocked}
          pushPayloadTypeConstrained={existingPush?.constraints?.pushPayloadTypeId?.isReadOnly}
          isReadonly={isReadonly}
          onChangePushField={onChangePushField}
          onSelectPayloadType={onSelectPayloadType}
          onChangeRuleField={onChangeRuleField}
        />
        {showContentForm && (
          <ContentForm
            contentDraft={draft.content}
            stepIdx={stepIdx}
            isJourneyLocked={isJourneyLocked}
            contentManagerSingleSelectOnly={!isJourneyLocked}
            stepPushRefId={stepPushRefId}
            readOnly={isReadonly}
            onSelectContent={onSelectContent}
            onRemoveContent={onRemoveContent}
          />
        )}
      </SlidingSidePanel>
    </div>
  );
}

function PushSidePanel() {
  const isStandaloneSlideoutsEnabled = useFeatureFlag('tx_standalone_slideouts');

  if (!isStandaloneSlideoutsEnabled) {
    return <PushSidePanelLegacy />;
  }

  return <PushSidePanelStandalone />;
}

export default PushSidePanel;
